"use client";
import Image from "next/image";
import React from "react";
import { delay, motion } from "framer-motion";

const FullImge = () => {
  const ParaContent = [
    {
      id: 1,
      title: "🏰 Ayodhya",
      subtitle: "Birthplace of Rama",
      imgsrc: "/assets/city--ayodhya.png",
      para: "Ayodhya is the holy city where Lord Rama was born to King Dasharatha after the Putrakameshti yajna. It is described as a land of peace, prosperity, and dharma. The city became the center of Ram Rajya, an ideal kingdom where truth and justice guided every decision. Ayodhya is not just a place of birth, but a symbol of hope and righteousness that continues to inspire people even today.",
    },
    {
      id: 2,
      title: "🌼 Mithila",
      subtitle: "Sita's Swayamvar",
      imgsrc: "/assets/city--mithila.png",
      para: "Mithila is remembered as the land of King Janaka, where Sita's swayamvar was held. Rama, with divine strength and humility, broke Lord Shiva's mighty bow to win Sita's hand in marriage. This event was not only a union of two noble souls but also a moment that showed how virtue and devotion triumph over pride. Mithila stands as a symbol of love, destiny, and the blessings of dharma.",
    },
    {
      id: 3,
      title: "🌳 Panchavati",
      subtitle: "Forest Exile",
      imgsrc: "/assets/city--panchvati.png",
      para: "Panchavati was the forest home of Rama, Sita, and Lakshmana during their years of exile. It was here that Shurpanakha's encounter led to conflict, Maricha appeared as the golden deer, and Ravana abducted Sita. Jatayu, the noble bird, gave his life trying to protect her. Panchavati represents struggle, sacrifice, and the turning point where the story moves from peace to battle.",
    },
    {
      id: 4,
      title: "🐒 Kishkindha",
      subtitle: "Sugriva Alliance",
      imgsrc: "/assets/city--kiskindha.png",
      para: "Kishkindha is the kingdom of the Vanaras where Rama formed a bond of friendship with Sugriva. By defeating Vali, Rama helped Sugriva regain his throne, and in return, Sugriva promised to aid in the search for Sita. Kishkindha highlights the importance of alliances, loyalty, and the strength that comes when friends unite for a noble cause.",
    },
    {
      id: 5,
      title: "🏰 Lanka",
      subtitle: "Ravana's Kingdom",
      imgsrc: "/assets/city--lanka.png",
      para: "Golden Lanka, ruled by Ravana, was a city of wealth, brilliance, and power. Yet, despite its grandeur, it became the stage for arrogance and downfall. Ravana's ego blinded him, leading to the great war against Rama. Lanka symbolizes how pride and greed can destroy even the most prosperous kingdoms, while dharma always finds victory.",
    },
    {
      id: 6,
      title: "🌺 Ashok Vatika",
      subtitle: "Sita's Captivity",
      imgsrc: "/assets/ashok-vatika.avif",
      para: "Ashok Vatika was the garden in Lanka where Sita was kept captive by Ravana. Despite fear and hardship, Sita remained firm in her devotion to Rama and her faith in dharma. Her courage and purity turned this place into a symbol of strength, resilience, and the power of inner faith. Ashok Vatika reminds us that true devotion can withstand even the darkest trials.",
    },
    {
      id: 7,
      title: "🌊 Rameshwaram",
      subtitle: "Setubandh Bridge",
      imgsrc: "/assets/city--rameshwaram.png",
      para: "At Rameshwaram, Rama and the Vanara army built the legendary stone bridge across the ocean to reach Lanka. This incredible feat showed the power of unity, teamwork, and divine guidance. The Setubandh bridge is remembered as a miracle of faith and determination, proving that when people come together for a righteous cause, even the impossible becomes possible.",
    },
  ];

  const motionConfigs = {
    1: {
      initial: { opacity: 0, y: 100 },
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.7 },
    },
    2: {
      initial: { opacity: 0, x: -50 },
      animate: { opacity: 1, x: 0 },
      transition: { duration: 0.7 },
    },
    3: {
      initial: { scale: 0.8, opacity: 0 },
      animate: { scale: 1, opacity: 1 },
      transition: { duration: 0.7 },
    },
    4: {
      initial: { rotate: -10, opacity: 0 },
      animate: { rotate: 0, opacity: 1 },
      transition: { duration: 0.7 },
    },
    5: {
      initial: { opacity: 0, x: 50 },
      animate: { opacity: 1, x: 0 },
      transition: { duration: 0.7 },
    },
    6: {
      initial: { opacity: 0, y: -50 },
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.7, delay: 0.3 },
    },
    7: {
      initial: { scale: 0.5, opacity: 0 },
      animate: { scale: 1, opacity: 1 },
      transition: { duration: 0.9, delay: 0.3 },
    },
  };

  return (
    <section
      id="geography"
      className="bg-[#f7f3e9] py-12 px-5 md:px-18 mx-auto flex flex-col gap-10 font-[Inter] text-[#222222]"
    >
      <h1 className="text-center text-2xl md:text-4xl font-[Cinzel] font-bold text-[#1e3a8a] md:mb-12 select-none">
        Sacred Geography of Ramayan
      </h1>
      <div className="flex flex-col md:gap-8 gap-7">
        {ParaContent.map(({ id, title, subtitle, imgsrc, para }, index) => (
          <div
            key={id}
            className={`flex flex-col md:flex-row md:gap-x-8 pb-8 md:pb-6 ${
              index % 2 === 0 ? "md:flex-row-reverse" : ""
            } `}
          >
            <motion.div
              initial={motionConfigs[id].initial}
              whileInView={motionConfigs[id].animate}
              transition={motionConfigs[id].transition}
              viewport={{ once: true, amount: 0.57 }}
              className="flex justify-center items-center h-full"
            >
              <Image
                src={imgsrc}
                alt={title}
                width={400}
                height={400}
                loading="lazy"
                className="object-cover w-[80vw] h-full rounded-xl flex-1 md:w-[47vw] md:h-[40vh] lg:h-[60vh]"
              />
            </motion.div>

            <div className="flex flex-1 flex-col  gap-2 md:gap-3 justify-start items-start p-6">
              <h2 className="text-2xl font-[Cinzel] text-[#1e3a8a] font-semibold select-none">
                {title}
              </h2>
              <h2 className="text-lg md:text-2xl font-[Cinzel] text-[#1e3a8a] font-semibold select-none">
                {subtitle}
              </h2>
              <p className="text-sm md:text-lg font-[Inter] max-w-md text-[#222222] md:leading-snug leading-snug tracking-[1px] select-none">
                {para}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default FullImge;
